import tweeners from '../tweeners'
import Animation from 'animation'
import * as decorations from '../decorations'
import * as d3 from 'd3-selection'
import * as scale from 'd3-scale'
import { interpolate } from 'd3-interpolate'
import * as array from 'd3-array'
import * as shape from 'd3-shape'
import { scaleLinear } from 'd3-scale'
import { EPSILON } from 'chart-constants'
import * as colors from 'colors'

class BarAnimation extends Animation {

  constructor (options) {
    super(options)
    this.strokeWidth = options.strokeWidth
  }

  configure (axis, serie) {
    super.configure(axis, serie)

    if (!this.yScale) {
      // If there isn't a yscale, that means the animation is running and all bars are being removed
      // and there's no longer a y-axis. Just use the previous yscale
      this.yScale = this.previous.yScale
    }

    // Calculate the stroke width based on the width of the bars
    let strokeScale = scale.scaleLinear()
                           .domain([3, 17]) // bars less than 3 pixels won't have a stroke. Bars 17px wide and above will have the full stroke width
                           .range([0.5, 2]) // max (and most times) stroke width of 2
                           .clamp(true)
      , strokeWidth = strokeScale(this.serieScale.bandwidth())

    this.strokeWidth = strokeWidth // Set the stroke width
    this.tweeners = tweeners(this.yScale, this.scalers.x, this.serieScale, serie, this.strokeWidth, this.labelsConfig)

    this.exitingSeries.each((serie, i, all) => {
      // The serie is being removed, so all points are being removed
      serie.points.forEach(point => {
        // If removing multiple series, we assume an entire stack was removed since we don't support
        // removing multiple series at once (See #349), so send everything to 0. Otherwise, send to the base location
        let val = all.length > 1 ? 0 : point._range[0]

        // So decoration code knows to fade out the top/bottom strokes
        delete point._topStack
        delete point._bottomStack
        point._range = [val, val] // Sets the _range so its height will goto 0.
      })
    })

    this.serieColor = serie.color

    return this
  }

  bindAndAnimate (container, node) {
    let serie = d3.select(node).datum()
      , svg = d3.select(node.ownerSVGElement)
      , group = d3.select(node)
                  .selectAll('g.rect')
                  .data(d => d.points.map(p => p), d => `${d.rangeId}-${serie.id}`) // Maps all points so we create a copy of bound, no filting so we always have rects in the DOM
      , enter = group.enter()
                     .append('g') // Add the rect group components
      , transition = group.merge(enter)
                          .attr('class', d => d.color || serie.color) // If the point color isn't defined, fallback to the serie's color
                          .attr('clip-path', `url(#${svg.attr('id')}-chart-clip-path-plot-height-${this.plotId})`)
                          .classed('rect', true)
                          .transition(container)
      , exit = group.exit()
                    .transition(container) // rects that are leaving

    this.animate(serie, enter, group, transition, exit)

    this._setColors(transition.selection())
  }

  _setColors (selection) {
    let serieColor = this.serieColor
    selection.each(function (d) {
      let color = colors.lookupObj(d.color || serieColor)

      d3.select(this)
        .selectAll('.bar-label')
        .classed('light dark', false)
        .classed(colors.getTextVariation(color.color), true)
        .filter('.above') // If it's outside it's always dark
        .classed('light', false)
        .classed('dark', true)

      d3.select(this)
        .selectAll('rect')
        .style('fill', colors.mixWhite(color.rgba, .3))
    })
  }

  /*
   * Receives a selection, `group`, and returns its `rect`'s dimensions
   */
  _getDimensions (group) {
    let rect = group.select('rect')
    return {
      x: +rect.attr('x'),
      width: +rect.attr('width'),
      y: +rect.attr('y') + 1,
      height: +rect.attr('height') + 1
    }
  }

  /*
   * Inserts the `entering` selection in the DOM with zero height. Used in situations
   * where the animation forces the nodes to grow.
   */
  _insertZeroHeight (entering, serie) {
    entering.append('rect')
            .attr('y', d => {
              if (serie.stackBy && this.previous.yScale) {
                 // Stacked, grab determine its start location by running its value through the previous
                 // scale function
                 return this.previous.yScale(Math.max(0, d._range[0]))
               } else {
                 // Start at the bottom
                 return this.yScale(Math.max(0, d._range[0]))
               }
            })
            .attr('height', 0)
            .attr('width', d => this.serieScale.bandwidth()) // start at correct width
            .attr('x', d => this.scalers.x.groupScale(d.rangeId) + this.serieScale(serie._columnIdx)) // start in the correct position

    return entering
  }

  /*
   * Inserts the `entering` selection the in the DOM at its final position.
   * The selection will be hidden
   */
  _insertHidden (entering, serie) {
    // Put the new node in the DOM in its final position, so we can grab its dimensions
    entering.append('rect')
            .attr('x', d => this.scalers.x.groupScale(d.rangeId) + this.serieScale(serie._columnIdx))
            .attr('width', this.serieScale.bandwidth())
            .attr('y', d => this.yScale(Math.max(0, d._range[1])))
            .attr('height', d => Math.abs(this.yScale(d._range[0]) - this.yScale(d._range[1])))

    entering.style('opacity', EPSILON)
            .call(decorations.default, this.strokeWidth)
            .call(decorations.position, this.strokeWidth, {}, this.labelsConfig)

    return entering
  }

  /*
   * Scales the `selection` of points into the `box` and
   * returns the scale function
   */
  _boxScale (selection, box) {
    let data = Object.assign({}, selection)
      , stack = shape.stack()
                     .keys(Object.keys(data))
                     .value((d, key) => d[key].raw)([data])
      , min = array.min(stack, series => array.min(series[0]))
      , max = array.max(stack, series => array.max(series[0]))

    stack.forEach(serie => {
           let range = serie[0]
             , point = range.data[serie.index] // Grab the actual point

           // Don't override `_range` which is based on the full y-axis scale
           // Set a range `_boxRange` which maps to the scaler used.
           point._boxRange = [serie[0][0] || 0, serie[0][1] || 0]
         })

    stack.filter(serie => serie[0][1] - serie[0][0]) // Remove series w/o a height
         .forEach((serie, i, all) => {
           // Tell this point it's the top and/or bottom
           let point = serie[0].data[serie.index]
           point._bottomStack = i === 0
           point._topStack = i === all.length - 1
         })

    return scaleLinear().domain([min, max])
                        .range([Math.max(0, box.height) + box.y, box.y])
  }

  /*
   * Joins the separated `selection` elements so they all stacked within the `box`
   */
  _join (selection, box) {
    let strokeWidth = this.strokeWidth
      , scale = this._boxScale(selection.data(), box)
      , labelsConfig = this.labelsConfig

    this.chart.yTransition.duration(this.timing.duration) // Sync the y-axis w/ the vertical transition

    return selection.transition()
                    .delay(this.timing.delay)
                    .duration(this.timing.duration)
                    .tween('vertical', function (d) {
                      let container = d3.select(this)
                        , rect = container.select('rect')
                        , y0 = rect.attr('y')
                        , y1 = scale(Math.max(0, d._boxRange[1]))
                        , h0 = Number(rect.attr('height'))
                        , h1 = Math.abs(scale(d._boxRange[0]) - scale(d._boxRange[1]))
                        , interpolateHeight = interpolate(h0, h1)
                        , interpolateY = interpolate(y0, y1)

                      return t => {
                        let height = interpolateHeight(t)
                          , y = interpolateY(t)

                        rect.attr('y', y)
                        rect.attr('height', height)
                        container.call(decorations.position, strokeWidth, {y, height}, labelsConfig)
                      }
                    })
                    .transition()
                    .tween('decorations', this.tweeners.decorations) // Fade decorations
                    .tween('horizontal', function (d) {
                      let container = d3.select(this)
                        , rect = d3.select(this).select('rect')
                        , interpolateX = interpolate(+rect.attr('x'), box.x)
                        , interpolateWidth = interpolate(+rect.attr('width'), box.width)

                      return t => {
                        let width = interpolateWidth(t)
                          , x = interpolateX(t)

                        rect.attr('x', x)
                        rect.attr('width', width)

                        container.call(decorations.position, strokeWidth, {width, x}, labelsConfig)
                      }
                   })
  }

  /*
   * Receives `selection` of nodes that are currently stacked within the `box` and splits them
   */
  _split (selection, box) {
    let strokeWidth = this.strokeWidth
      , scale = this._boxScale(selection.data(), box)

    // Put the new nodes `rect`'s in the dom
    selection.append('rect')
               .attr('x', box.x)
               .attr('width', box.width)
               .attr('y', d => Math.max(0, scale(d._boxRange[1])))
               .attr('height', d => Math.abs(scale(d._boxRange[0]) - scale(d._boxRange[1])))

    // Adjust stacks, so just the top rect has a top border and the bottom stack has a bottom border
    selection.each((d, i, all) => {
      if (i === 0) {
        delete d._topStack
      } else if (i === all.length - 1) {
        delete d._bottomStack
      } else {
        delete d._topStack
        delete d._bottomStack
      }
    })

    // Show decorations immediately
    selection.call(decorations.default, strokeWidth)
             .call(decorations.position, strokeWidth, {}, this.labelsConfig)

    selection.each(d => {
      // Now they'll be a top and bottom stack, so they have all decorations
      d._topStack = true
      d._bottomStack = true
    })

    // Sync the y-axis w/ the vertical transition
    this.chart.yTransition
              .duration(this.timing.duration)
              .delay(this.timing.duration)

    return selection.transition()
                    .delay(this.timing.delay)
                    .duration(this.timing.duration)
                    .tween('decorations', this.tweeners.decorations) // So they decorations fade in during the horizontal movement
                    .tween('horizontal', this.tweeners.horizontal)
                    .transition()
                    .tween('vertical', this.tweeners.vertical)
  }

}

export default BarAnimation
